import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { HistoryStore, toTitleCase } from '@iotv/datamodel';
import { isScoreKeyLike, ReadingKeyToScoreKey } from '@iotv/iotv-v3-types';
import { Stack, Typography } from '@mui/material';
import { extractGraphData } from '../../data/TypeHelpers';
import { toPascalCase } from '../../helpers/format';
import { useKeySelector } from '../../hooks/useKeySelector';
import { HistoryLineChart } from '../../v2/components/charts/HistoryLineChart';
const debug = process.env.REACT_APP_DEBUG && false;
const batteryKeys = ['deviceBatteryVoltage', 'batteryPercentage', 'batteryRemainingDays', 'percentageDropRate'];
const readableBatteryKeys = {
    deviceBatteryVoltage: 'Battery (V)',
    batteryPercentage: 'Battery (%)',
    batteryRemainingDays: 'Remaining (days)',
    percentageDropRate: 'Drop (%)',
};
const networkKeys = ['fCnt', 'rssi', 'sf', 'snr'];
const readableNetworkKeys = {
    fCnt: 'Frame',
    rssi: 'RSSI',
    sf: 'SF',
    snr: 'SNR',
};
export default function ObjectGraphCard(props) {
    debug && console.log('ObjectGraphCard has props', props);
    const { thingStatesMostRecentFirst, filterGraphKeys } = props;
    /** set up history store */
    const historyObject = thingStatesMostRecentFirst
        ? new HistoryStore(extractGraphData(thingStatesMostRecentFirst))
        : props.historyObject instanceof HistoryStore
            ? new HistoryStore(props.historyObject)
            : new HistoryStore();
    /** battery */
    const availableBatteryKeys = historyObject.keys().filter((k) => batteryKeys.includes(k));
    const { requiredKeys: selectedBatteryKeys, component: batteryKeySelector } = useKeySelector({
        initialKeys: [...availableBatteryKeys],
        initialRequiredKeys: [...availableBatteryKeys],
        readableKeyNames: readableBatteryKeys,
    });
    const batteryChart = availableBatteryKeys.length ? (_jsxs(ChartCard, { name: "Battery", children: [_jsx(HistoryLineChart, { historyObject: historyObject, keys: selectedBatteryKeys, keyNameMap: (k) => readableBatteryKeys[k] }), batteryKeySelector] }, "battery")) : (_jsx(_Fragment, {}));
    /** network */
    const availableNetworkKeys = historyObject.keys().filter((k) => networkKeys.includes(k));
    const { requiredKeys: selectedNetworkKeys, component: networkKeySelector } = useKeySelector({
        initialKeys: [...availableNetworkKeys],
        initialRequiredKeys: [...availableNetworkKeys],
        readableKeyNames: readableNetworkKeys,
    });
    const networkChart = availableNetworkKeys.length ? (_jsxs(ChartCard, { name: "Network", children: [_jsx(HistoryLineChart, { historyObject: historyObject, keys: selectedNetworkKeys, keyNameMap: (k) => readableNetworkKeys[k] }), networkKeySelector] }, "network")) : (_jsx(_Fragment, {}));
    const deviceCharts = (_jsxs(Stack, { direction: props.isSmallView ? 'column' : 'row', gap: 1, children: [batteryChart, networkChart] }));
    /** readings (and scores) */
    const numberKeys = historyObject.keys().filter((k) => historyObject[k].every((v) => typeof v === 'number' || v === null || v === undefined));
    const scoreKeys = historyObject.keys().filter((k) => isScoreKeyLike(k));
    const readingKeys = filterGraphKeys ||
        Object.keys(historyObject)
            .filter((key) => numberKeys.includes(key))
            .filter((key) => !batteryKeys.includes(key))
            .filter((key) => !networkKeys.includes(key))
            .filter((key) => !scoreKeys.includes(key));
    const readingCharts = readingKeys.map((key) => {
        const readingChart = (_jsx(ChartCard, { name: key, children: _jsx(HistoryLineChart, { historyObject: historyObject, keys: [key], showLegend: false }) }, key));
        const scoreKey = ReadingKeyToScoreKey(key);
        if (!scoreKeys.includes(scoreKey))
            return readingChart;
        const scoreChart = (_jsx(ChartCard, { name: key, score: true, children: _jsx(HistoryLineChart, { historyObject: historyObject, keys: [scoreKey], showLegend: false }) }, scoreKey));
        return (_jsxs(Stack, { direction: props.isSmallView ? 'column' : 'row', gap: 1, children: [readingChart, scoreChart] }, key + scoreKey));
    });
    return (_jsxs(Stack, { gap: 1, children: [deviceCharts, readingCharts] }));
}
const ChartCard = ({ name, score = false, children }) => (_jsxs(Stack, { flex: 1, padding: 2, gap: 1, sx: { borderRadius: 2, backgroundColor: (t) => t.alpha(t.palette.primary.light, 0.5) }, children: [_jsxs(Typography, { variant: "h6", children: [score ? '[Score] ' : null, toTitleCase(toPascalCase(name))] }), children] }));
